import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';

/**
 * Минималистичный rich-text-редактор поверх `contentEditable`. Без
 * внешних зависимостей — нам нужны только базовые команды (жирный,
 * курсив, подчёркнутый, зачёркнутый, ссылка) и режим HTML-исходника.
 *
 * Для команд используется `document.execCommand`. Да, формально она
 * deprecated, но на практике работает во всех современных браузерах и
 * для простой админки этого достаточно. Если когда-нибудь понадобится
 * что-то серьёзное (таблицы, картинки, undo-стек) — заменим на Tiptap.
 *
 * Контракт:
 *   value (string, HTML)
 *   onChange(html: string)  — вызывается на blur/каждое нажатие
 *   placeholder, disabled, maxLength (на backend всё равно валидируем)
 *
 * При смене value снаружи обновляем innerHTML — но только если
 * редактор сейчас НЕ в фокусе, иначе перетрём ввод и сломаем каретку.
 */
const COMMANDS = [
  { cmd: 'bold',          label: 'B',  title: 'Жирный',       style: { fontWeight: 700 } },
  { cmd: 'italic',        label: 'I',  title: 'Курсив',       style: { fontStyle: 'italic' } },
  { cmd: 'underline',     label: 'U',  title: 'Подчёркнутый', style: { textDecoration: 'underline' } },
  { cmd: 'strikeThrough', label: 'S',  title: 'Зачёркнутый',  style: { textDecoration: 'line-through' } },
];

export default function RichTextarea({
  value, onChange, placeholder, disabled, minHeight = 120, maxLength,
}) {
  const editorRef = useRef(null);
  const [htmlMode, setHtmlMode] = useState(false);
  // Локальный буфер для HTML-режима — чтобы не дёргать onChange на каждый
  // символ и не перетирать каретку родительским рендером.
  const [htmlBuffer, setHtmlBuffer] = useState(value || '');

  // Синхронизация html ← value, когда пользователь снаружи поменял.
  useEffect(() => {
    if (htmlMode) { setHtmlBuffer(value || ''); return; }
    const el = editorRef.current;
    if (!el) return;
    const isFocused = document.activeElement === el;
    if (!isFocused && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, htmlMode]);

  const exec = (cmd, arg) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    // execCommand НЕ дёргает input-event при некоторых браузерах —
    // вытаскиваем innerHTML и сообщаем наверх.
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const insertLink = () => {
    if (disabled) return;
    const url = window.prompt('Адрес ссылки (URL):', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const onInput = () => {
    let html = editorRef.current?.innerHTML || '';
    if (maxLength && html.length > maxLength) {
      html = html.slice(0, maxLength);
      editorRef.current.innerHTML = html;
    }
    onChange?.(html);
  };

  const switchToHtml = () => {
    setHtmlBuffer(editorRef.current?.innerHTML || value || '');
    setHtmlMode(true);
  };
  const switchToWysiwyg = () => {
    setHtmlMode(false);
    onChange?.(htmlBuffer);
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 6,
      background: 'var(--panel)',
      overflow: 'hidden',
      opacity: disabled ? 0.6 : 1,
    }}>
      {/* Тулбар */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 6px', borderBottom: '1px solid var(--border)',
        background: 'var(--panel-2)',
      }}>
        {COMMANDS.map((c) => (
          <ToolbarBtn key={c.cmd} disabled={disabled} title={c.title} onClick={() => exec(c.cmd)}>
            <span style={c.style}>{c.label}</span>
          </ToolbarBtn>
        ))}
        <span style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 4px' }} />
        <ToolbarBtn disabled={disabled} title="Ссылка" onClick={insertLink}>
          <Icon name="link" size={12} />
        </ToolbarBtn>
        <ToolbarBtn disabled={disabled} title="HTML-исходник" active={htmlMode}
                    onClick={() => htmlMode ? switchToWysiwyg() : switchToHtml()}>
          {'<>'}
        </ToolbarBtn>
      </div>

      {htmlMode ? (
        <textarea
          value={htmlBuffer}
          onChange={(e) => setHtmlBuffer(e.target.value)}
          onBlur={() => onChange?.(htmlBuffer)}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          style={{
            display: 'block', width: '100%', minHeight,
            padding: 10, border: 'none', outline: 'none', resize: 'vertical',
            fontFamily: 'monospace', fontSize: 12.5,
            background: 'transparent', color: 'var(--fg)',
          }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={onInput}
          onBlur={onInput}
          onPaste={(e) => {
            // Чистый paste без форматирования — стандартный execCommand
            // пытается тащить style из источника, что часто ломает вёрстку.
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
          }}
          data-placeholder={placeholder}
          style={{
            display: 'block', minHeight, padding: 10,
            outline: 'none', fontSize: 13, lineHeight: 1.45,
          }}
        />
      )}

      {/* Стиль для placeholder через data-attr (минимальный CSS-in-JS). */}
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: var(--fg-faint, #999);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, disabled, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        minWidth: 26, height: 26, padding: '0 6px',
        border: '1px solid transparent',
        background: active ? 'var(--panel)' : 'transparent',
        borderColor: active ? 'var(--border)' : 'transparent',
        borderRadius: 4,
        cursor: disabled ? 'default' : 'pointer',
        color: 'var(--fg)',
        fontSize: 12,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseDown={(e) => e.preventDefault()} // чтобы не терять выделение в редакторе
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--panel)'; }}
      onMouseLeave={(e) => { if (!disabled && !active) e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}
